import { useCallback } from "react";
import type { Form, FormField, FormResponse } from "@tellescope/types-models";
import { useIsNewFormsExperienceEnabled } from "../../../../shared/store/portalSchemaStore";
import { enduserQueryClient } from "../../../components/enduserQueryClient";
import { useFormGroupContinuation } from "./useFormGroupContinuation";

interface UsePendingFormViewArgs {
  formResponse: FormResponse;
  form: Form;
  fields: Pick<FormField, "type">[];
}

export const usePendingFormView = ({ formResponse, form, fields }: UsePendingFormViewArgs) => {
  const isNewFormsExperience = useIsNewFormsExperienceEnabled();
  const continuation = useFormGroupContinuation({ formResponse, form });
  const { continueAfterSubmit } = continuation;

  // The legacy renderer also posts "Form Submitted" when a Redirect field saves its form on the way
  // out, and chaining then would pull the enduser into the next group form mid-redirect.
  const hasRedirectField = fields.some((field) => field.type === "Redirect");

  const handleIframeSubmitted = useCallback(() => {
    // Lists only: refetching this response would swap the iframe's own thanks screen for the
    // read-only view.
    void enduserQueryClient.invalidateQueries({ queryKey: ["form_responses", "getSome"] });
    if (!hasRedirectField) continueAfterSubmit();
  }, [hasRedirectField, continueAfterSubmit]);

  return {
    ...continuation,
    usesNewFormsExperience: isNewFormsExperience(formResponse.formId),
    handleIframeSubmitted,
  };
};
